import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Receipt, Download, IndianRupee, TrendingUp } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const ExpensesManager = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [expenses, setExpenses] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const protectedExport = useProtectedExport();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filter states
  const [filterHeader, setFilterHeader] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [formData, setFormData] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    header_id: '',
    description: '',
    amount: '',
    payment_mode: 'Cash',
    reference_number: '',
    notes: ''
  });

  const paymentModes = ['Cash', 'Bank Transfer', 'UPI', 'Credit Card', 'Cheque'];

  const fetchExpenses = useCallback(async () => {
    try {
      let url = '/expenses/';
      const params = new URLSearchParams();
      if (filterHeader) params.append('header_id', filterHeader);
      if (filterMode) params.append('payment_mode', filterMode);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (params.toString()) url += `?${params.toString()}`;

      const [expensesRes, summaryRes] = await Promise.all([
        api.get(url),
        api.get(`/expenses/summary?${params.toString()}`)
      ]);
      
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch expenses');
    } finally {
      setLoading(false);
    }
  }, [filterHeader, filterMode, fromDate, toDate]);

  const fetchHeaders = useCallback(async () => {
    try {
      const response = await api.get('/expense-headers/');
      setHeaders(response.data.filter(h => h.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch headers');
    }
  }, []);

  useEffect(() => {
    fetchExpenses();
    fetchHeaders();
  }, [fetchExpenses, fetchHeaders]);

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        expense_date: expense.expense_date,
        header_id: expense.header_id,
        description: expense.description || '',
        amount: expense.amount,
        payment_mode: expense.payment_mode || 'Cash',
        reference_number: expense.reference_number || '',
        notes: expense.notes || ''
      });
    } else {
      setSelectedExpense(null);
      setFormData({
        expense_date: new Date().toISOString().split('T')[0],
        header_id: '',
        description: '',
        amount: '',
        payment_mode: 'Cash',
        reference_number: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.header_id) {
      toast.error('Please select an expense category');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('expense_date', formData.expense_date);
      params.append('header_id', formData.header_id);
      params.append('amount', formData.amount);
      params.append('description', formData.description);
      params.append('payment_mode', formData.payment_mode);
      params.append('reference_number', formData.reference_number);
      params.append('notes', formData.notes);

      if (selectedExpense) {
        await api.put(`/expenses/${selectedExpense.id}?${params.toString()}`);
        toast.success('Expense updated successfully');
      } else {
        await api.post(`/expenses/?${params.toString()}`);
        toast.success('Expense created successfully');
      }
      setDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (expense) => {
    if (!window.confirm('Delete this expense?')) return;

    try {
      await api.delete(`/expenses/${expense.id}`);
      toast.success('Expense deleted');
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterHeader) params.append('header_id', filterHeader);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      const response = await api.get(`/expenses/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Expenses exported');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const clearFilters = () => {
    setFilterHeader('');
    setFilterMode('');
    setFromDate('');
    setToDate('');
    setSearchTerm('');
  };

  const filteredExpenses = expenses.filter(expense =>
    expense.expense_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.header_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Expenses</h1>
          <p className="text-gray-600">Track and manage company expenses</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => protectedExport.requestExport(handleExport)} style={{display: isAdmin ? undefined : 'none'}}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <IndianRupee className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Expenses</p>
                <p className="text-2xl font-bold text-red-600">₹{summary.total_amount?.toLocaleString('en-IN') || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Entries</p>
                <p className="text-2xl font-bold">{summary.total_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Top Category</p>
                <p className="text-lg font-bold truncate">
                  {summary.by_header?.[0]?.header_name || '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category-wise breakdown */}
      {summary.by_header?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Expenses by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              {summary.by_header.slice(0, 8).map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 truncate">{item.header_name}</p>
                  <p className="text-lg font-semibold">₹{item.amount?.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-gray-500">{item.count} entries</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterHeader || 'all'} onValueChange={(v) => setFilterHeader(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {headers.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterMode || 'all'} onValueChange={(v) => setFilterMode(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Payment Modes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment Modes</SelectItem>
                {paymentModes.map(mode => (
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              placeholder="From Date"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              placeholder="To Date"
            />
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Expenses ({filteredExpenses.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Expense #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Payment Mode</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.length > 0 ? (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-mono text-sm">{expense.expense_number}</TableCell>
                    <TableCell>{new Date(expense.expense_date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{expense.header_name}</Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">{expense.description || '-'}</TableCell>
                    <TableCell className="text-right font-medium text-red-600">
                      ₹{expense.amount?.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell>{expense.payment_mode}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(expense)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(expense)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                    No expenses found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedExpense ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select
                    value={formData.header_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, header_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select category</SelectItem>
                      {headers.map(h => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of expense"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label>Payment Mode</Label>
                  <Select
                    value={formData.payment_mode}
                    onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentModes.map(mode => (
                        <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Bill/Receipt number"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {selectedExpense ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default ExpensesManager;
